import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { formatDateForDisplay } from "@/lib/utils";
import { SpecialEventMediaItem } from "@/components/SpecialEventMediaItem";
import { SpecialEventLightbox } from "@/components/SpecialEventLightbox";

interface SpecialEvent {
  id: string;
  title: string;
  description: string | null;
  event_date: string | null;
  display_order: number;
  created_at: string;
}

interface SpecialEventMedia {
  id: string;
  event_id: string;
  title: string;
  description: string | null;
  image_url: string;
  video_url: string | null;
  media_type: string;
  display_order: number;
}

const SpecialEvents = () => {
  const { t } = useLanguage();
  const [events, setEvents] = useState<SpecialEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<SpecialEvent | null>(null);
  const [eventMedia, setEventMedia] = useState<SpecialEventMedia[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedMediaIndex, setSelectedMediaIndex] = useState<number | null>(null);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("special_events")
      .select("*")
      .order("display_order", { ascending: true });

    if (!error && data) {
      setEvents(data);
      // Auto-select first event if available
      if (data.length > 0) {
        handleSelectEvent(data[0]);
      }
    }
    setIsLoading(false);
  };

  const handleSelectEvent = async (event: SpecialEvent) => {
    setSelectedEvent(event);
    const { data, error } = await supabase
      .from("special_event_media")
      .select("*")
      .eq("event_id", event.id)
      .order("display_order", { ascending: true });

    if (!error && data) {
      setEventMedia(data);
    }
  };

  const openLightbox = (index: number) => {
    setSelectedMediaIndex(index);
  };

  const closeLightbox = () => {
    setSelectedMediaIndex(null);
  };

  const goToPrevious = () => {
    if (selectedMediaIndex !== null && selectedMediaIndex > 0) {
      setSelectedMediaIndex(selectedMediaIndex - 1);
    }
  };

  const goToNext = () => {
    if (selectedMediaIndex !== null && selectedMediaIndex < eventMedia.length - 1) {
      setSelectedMediaIndex(selectedMediaIndex + 1);
    }
  };

  const selectedMedia = selectedMediaIndex !== null ? eventMedia[selectedMediaIndex] : null;

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-12">
        <div className="container mx-auto px-4">
          {/* Hero Section */}
          <section className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4 text-primary">
              {t("specialevents.title")}
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t("specialevents.description")}
            </p>
          </section>

          {isLoading ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t("common.loading")}</p>
            </div>
          ) : events.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-muted-foreground">{t("specialevents.noevents")}</p>
            </div>
          ) : (
            <>
              {/* Event Selector */}
              {events.length > 1 && (
                <section className="mb-12">
                  <div className="flex flex-wrap gap-3 justify-center">
                    {events.map((event) => (
                      <Button
                        key={event.id}
                        onClick={() => handleSelectEvent(event)}
                        variant={selectedEvent?.id === event.id ? "default" : "outline"}
                        size="lg"
                      >
                        {event.title}
                      </Button>
                    ))}
                  </div>
                </section>
              )}

              {/* Selected Event Details */}
              {selectedEvent && (
                <>
                  <section className="text-center mb-8">
                    <h2 className="text-3xl font-bold mb-2">{selectedEvent.title}</h2>
                    {selectedEvent.description && (
                      <p className="text-muted-foreground mb-2">{selectedEvent.description}</p>
                    )}
                    {selectedEvent.event_date && (
                      <p className="text-sm text-muted-foreground">
                        {formatDateForDisplay(selectedEvent.event_date)}
                      </p>
                    )}
                  </section>

                  {/* Gallery Grid */}
                  <section className="mb-16">
                    <h3 className="text-2xl font-bold mb-8 text-center">
                      {t("specialevents.gallery")}
                    </h3>
                    {eventMedia.length === 0 ? (
                      <div className="text-center py-12">
                        <p className="text-muted-foreground">No media available for this event</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {eventMedia.map((media, index) => (
                          <Card
                            key={media.id}
                            className="overflow-hidden hover:shadow-lg transition-shadow"
                          >
                            <SpecialEventMediaItem
                              imageUrl={media.image_url}
                              videoUrl={media.video_url}
                              title={media.title}
                              mediaType={media.media_type}
                              onClick={() => openLightbox(index)}
                            />
                            <div className="p-4">
                              <h4 className="text-xl font-semibold mb-2">{media.title}</h4>
                              {media.description && (
                                <p className="text-muted-foreground">{media.description}</p>
                              )}
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </section>
                </>
              )}
            </>
          )}
        </div>
      </main>

      {/* Lightbox Modal */}
      {selectedMediaIndex !== null && selectedMedia && (
        <SpecialEventLightbox
          media={selectedMedia}
          currentIndex={selectedMediaIndex}
          totalCount={eventMedia.length}
          onClose={closeLightbox}
          onPrevious={goToPrevious}
          onNext={goToNext}
        />
      )}

      <Footer />
    </div>
  );
};

export default SpecialEvents;
